import _ from 'underscore';
import type { FetchPolicyValue } from '../../public-types';
import type { Middleware } from '../request-with-middleware';
import {
  DEFAULT_STORE_EXPIRATION_DURATION,
  FetchPolicy,
  getDataFromStore,
  getFetchPolicy,
  IsNetworkPolicy,
  IsStorePolicy,
  IsStoreSecondaryPolicy,
  shouldForceNetwork,
  updateDataRefStatus,
} from './fetch-policy-utils';

export interface FetchPolicyMiddlewareOptions {
  fetchPolicy?: FetchPolicyValue;
  cacheExpirationDuration?: number;
}

function getVariablesFetchPolicy(variables: object): FetchPolicyValue | undefined {
  if (!('options' in variables)
    || typeof variables.options !== 'object'
    || variables.options === null
    || !('fetchPolicy' in variables.options)) {
    return undefined;
  }
  const policy = variables.options.fetchPolicy;
  return Object.values(FetchPolicy).includes(policy as FetchPolicyValue)
    ? policy as FetchPolicyValue
    : undefined;
}

export function createFetchPolicyMiddleware({
  fetchPolicy = FetchPolicy.StoreOrNetwork,
  cacheExpirationDuration = DEFAULT_STORE_EXPIRATION_DURATION,
}: FetchPolicyMiddlewareOptions = {}): Middleware {
  return next => async request => {
    const requestFetchPolicy = getFetchPolicy(
      request.spec.specType,
      getVariablesFetchPolicy(request.variables) || request.spec.requestContract.fetchPolicy,
      fetchPolicy,
    );
    const isStoreSecondaryPolicy = IsStoreSecondaryPolicy(requestFetchPolicy);
    let dataInStore = null;
    const isNetworkPolicy = IsNetworkPolicy(requestFetchPolicy);
    const isStorePolicy = IsStorePolicy(requestFetchPolicy);
    const currentTimestamp = Date.now();
    const variablesWithoutOptions = _.omit(request.variables, 'options');
    const isForceNetwork = shouldForceNetwork({
      store: request.store,
      spec: request.spec,
      dataRefId: request.dataRefId,
      variables: variablesWithoutOptions,
      storeExpirationDuration: cacheExpirationDuration,
      currentTimestamp,
      requestFetchPolicy,
    });

    if (isStorePolicy && !isForceNetwork && !isStoreSecondaryPolicy) {
      dataInStore = getDataFromStore(request.store, request.spec, request.dataRefId);
      if (!_.isEmpty(dataInStore)) {
        request.middlewareStates.isResponseFromStore = true;
        return dataInStore;
      }
    }

    if (isNetworkPolicy) {
      return next(request).then(response => {
        updateDataRefStatus({
          store: request.store,
          spec: request.spec,
          dataRefId: request.dataRefId,
          variables: variablesWithoutOptions,
          currentTimestamp,
        });
        return response;
      }).catch((error: unknown) => {
        if (requestFetchPolicy === FetchPolicy.NetworkOrStore) {
          dataInStore = getDataFromStore(request.store, request.spec, request.dataRefId);
          if (!_.isEmpty(dataInStore)) {
            request.middlewareStates.isResponseFromStore = true;
            return dataInStore;
          }
        }
        throw error;
      });
    }
    return next(request);
  };
}
